/**
 * IBS admission review (P97).
 *
 * Lists IBS candidates + their admission status; lets an operator run
 * the Marsad Engine to derive a placeholder admission decision
 * (scores + decision + threshold placeholders) and dual-sign it off
 * (CRO + business owner). The second sign-off flips the IBS status.
 */
import { refusalMessage } from "shared/api/refusalMessage";
import { canSignAs } from "shared/api/authority";
import { SignatureHistory } from "shared/components/SignatureHistory";
import { Bdi } from "shared/components/Bdi";
import { roleLabel } from "shared/vocab/workflowState";
import { formatDateTime } from "shared/time/formatDate";
import { useToast } from "shared/toast/ToastProvider";
import {
  seatHolder,
  useSeatHolders,
  type SeatHolders,
} from "shared/api/useSeatHolders";
import { canRunOperations } from "shared/api/authority";
import { holdsSeat, useMe } from "shared/api/useMe";
import { useEffect, useState } from "react";
import { Explainer } from "shared/components/Explainer";
import { Link, useSearchParams } from "react-router-dom";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { outcomeCategoryLabel, stateLabel } from "shared/vocab/workflowState";

import type {
  IBSAdmissionDecision,
  IBSCandidate,
  IBSReviewRow,
} from "shared/api/types";
import {
  useChallengeAdmission,
  useDeriveAdmission,
  useIBSCandidates,
  useIBSReviewScan,
  useRequestRemoval,
  useSignoffAdmission,
} from "twin/api/ibs_admission_hooks";
import { EngineOffBanner } from "shared/components/EngineOffBanner";
import { ManualAdmissionScores } from "twin/ibs/ManualAdmissionScores";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { formatSAR } from "shared/money/money";
import { EngineLanguageNote } from "shared/components/EngineLanguageNote";
import { PromoteServiceForm } from "twin/ibs/PromoteServiceForm";

const REVIEW_STATUS_PILL: Record<string, string> = {
  overdue: "bg-rag-redBg text-rag-red",
  due_soon: "bg-rag-amberBg text-rag-amber",
  current: "bg-rag-greenBg text-rag-green",
  unscheduled: "bg-slate-100 text-slate-600",
};

const DRIFT_PILL: Record<string, string> = {
  promoted: "bg-rag-redBg text-rag-red",
  demoted: "bg-rag-greenBg text-rag-green",
  stable: "bg-slate-100 text-slate-600",
  new: "bg-slate-100 text-slate-600",
};

const STATUS_PILL: Record<string, string> = {
  candidate: "bg-slate-100 text-slate-600",
  admitted: "bg-rag-greenBg text-rag-green",
  removed: "bg-rag-redBg text-rag-red",
};

const DECISION_PILL: Record<string, string> = {
  included: "bg-rag-greenBg text-rag-green",
  excluded: "bg-rag-redBg text-rag-red",
  deferred: "bg-rag-amberBg text-rag-amber",
};

// P114.1/.3: criticality tier -> badge tone + operating profile (mirrors
// twin/ibs_methodology.py's _CRITICALITY_BY_SEVERITY, runbook section 7.3).
const TIER_META: Record<
  string,
  { label: string; tone: string; bizCrit: string; drill: string; refresh: string }
> = {
  tier_0: {
    label: "Tier 0 · Critical",
    tone: "bg-rag-redBg text-rag-red",
    bizCrit: "0",
    drill: "Annual bank-wide drill",
    refresh: "Quarterly",
  },
  tier_1: {
    label: "Tier 1 · High",
    tone: "bg-rag-amberBg text-rag-amber",
    bizCrit: "1",
    drill: "Annual tabletop",
    refresh: "Quarterly",
  },
  tier_2: {
    label: "Tier 2 · Medium",
    tone: "bg-rag-amberBg text-rag-amber",
    bizCrit: "2",
    drill: "Annual tabletop",
    refresh: "Annually",
  },
  tier_3: {
    label: "Tier 3 · Standard",
    tone: "bg-slate-100 text-slate-600",
    bizCrit: "3",
    drill: "Methodology review only",
    refresh: "Annually",
  },
};

// Per-criterion labels + 0-5 anchor bands (mirrors IBS_CRITERION_META).
const CRITERION_LABEL: Record<string, string> = {
  // C6 -- the map covered eight ids and the estate stores thirteen, so
  // the rest fell through to the raw key and a business reader met
  // "q1_external_customer" and "c2_kingdom_specific" in a governance
  // record. Read off the decisions actually stored, not off a subset
  // of the methodology.
  // The TRIGGERS, which name conditions rather than criteria and were
  // rendering raw beside the decision.
  severity_irreversibility: "Severity and irreversibility",
  containment: "Containment",
  composite: "Composite score",
  systemic_risk: "Systemic risk",
  firm_safety: "Safety of the firm",
  q1_external_customer: "Serves an external customer",
  q2_client_impact: "Client impact if it fails",
  c1_regulatory_instrument: "Named in a regulatory instrument",
  c2_kingdom_specific: "Specific to the Kingdom",
  c3_transactional: "Transactional",
  severity: "Severity",
  breadth: "Breadth",
  time_to_impact: "Time to impact",
  substitutability: "Substitutability",
  financial_stability: "Financial stability",
  time_to_breach: "Time to breach",
  penalty_severity: "Penalty severity",
  reversibility: "Reversibility",
};

const CRITERION_ANCHORS: Record<string, string[]> = {
  severity: ["none", "trivial", "notable", "near tolerance", "at risk", "breached"],
  breadth: ["<1k", "1k-10k", "10k-100k", "100k-500k", "500k-1m", ">=1m"],
  time_to_impact: [">1wk", ">72h", "24-72h", "12-24h", "4-12h", "<4h"],
  substitutability: [
    "reliable",
    "minor friction",
    "partial",
    "weak",
    "untested",
    "none",
  ],
  financial_stability: ["<1m", "1m-10m", "10m-100m", "100m-500m", "500m-1bn", ">=1bn"],
  time_to_breach: [">1wk", ">72h", "48-72h", "24-48h", "12-24h", "<12h"],
  penalty_severity: [
    "advisory",
    "minor",
    "material",
    "significant",
    "licence cond.",
    "licence risk",
  ],
  reversibility: [
    "full",
    "low effort",
    "with effort",
    "hard",
    "barely",
    "irreversible",
  ],
};

function ReviewsDuePanel(): JSX.Element | null {
  const { t } = useTranslation();
  const me = useMe();
  const scan = useIBSReviewScan(canRunOperations(me.data));
  // Best-effort: stay invisible while loading or on error (the panel is
  // supplementary to the register table below).
  if (!scan.data) return null;
  const { overdue_count, due_soon_count, rows } = scan.data;
  const flagged = rows.filter(
    (r: IBSReviewRow) =>
      r.review_status === "overdue" || r.review_status === "due_soon",
  );

  return (
    <section
      data-testid="ibs-reviews-due"
      className="rounded-lg border border-slate-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">
          {t("modules.twin.ibsAdmission.review.heading")}
        </h2>
        <div className="flex items-center gap-2 text-xs">
          <span
            data-testid="ibs-reviews-overdue-count"
            className={`rounded px-2 py-0.5 ${REVIEW_STATUS_PILL.overdue}`}
          >
            {t("modules.twin.ibsAdmission.review.overdue", { count: overdue_count })}
          </span>
          <span className={`rounded px-2 py-0.5 ${REVIEW_STATUS_PILL.due_soon}`}>
            {t("modules.twin.ibsAdmission.review.dueSoon", { count: due_soon_count })}
          </span>
        </div>
      </div>
      <p className="mt-1 max-w-3xl text-xs text-slate-500">
        {t("modules.twin.ibsAdmission.review.intro")}
      </p>
      {flagged.length === 0 ? (
        <p
          data-testid="ibs-reviews-all-current"
          data-admitted={rows.length > 0 ? "true" : "false"}
          className="mt-3 text-sm text-slate-500"
        >
          {/* ROUND 15 B8 -- "All admitted IBSes are within their review
              cadence." with none admitted, fourth round. An all-clear
              over nothing is the class criterion 25 names. */}
          {rows.length > 0
            ? t("modules.twin.ibsAdmission.review.allCurrent")
            : t("modules.twin.ibsAdmission.review.nothingAdmittedYet")}
        </p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <thead className="text-start text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="py-1">{t("modules.twin.ibsAdmission.review.colIbs")}</th>
              <th className="py-1">{t("modules.twin.ibsAdmission.review.colTier")}</th>
              <th className="py-1">{t("modules.twin.ibsAdmission.review.colDue")}</th>
              <th className="py-1">
                {t("modules.twin.ibsAdmission.review.colStatus")}
              </th>
              <th className="py-1">{t("modules.twin.ibsAdmission.review.colDrift")}</th>
            </tr>
          </thead>
          <tbody>
            {flagged.map((r: IBSReviewRow) => (
              <tr
                key={r.decision_id}
                data-testid={`ibs-review-row-${r.ibs_code}`}
                className="border-t border-slate-100"
              >
                <td className="py-1.5 text-slate-800">{r.ibs_name}</td>
                <td className="py-1.5 text-slate-600">
                  {r.criticality_tier
                    ? (TIER_META[r.criticality_tier]?.label ?? r.criticality_tier)
                    : "--"}
                </td>
                <td className="py-1.5 tabular-nums text-slate-600">
                  {r.next_review_due ?? "--"}
                </td>
                <td className="py-1.5">
                  <span
                    className={`rounded px-2 py-0.5 text-xs ${
                      REVIEW_STATUS_PILL[r.review_status] ?? ""
                    }`}
                  >
                    {t(`modules.twin.ibsAdmission.review.status.${r.review_status}`)}
                  </span>
                </td>
                <td className="py-1.5">
                  {r.drift !== "stable" && r.drift !== "new" ? (
                    <span
                      className={`rounded px-2 py-0.5 text-xs ${DRIFT_PILL[r.drift] ?? ""}`}
                    >
                      {t(`modules.twin.ibsAdmission.review.drift.${r.drift}`)}
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">
                      {t(`modules.twin.ibsAdmission.review.drift.${r.drift}`)}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export function IBSAdmissionView(): JSX.Element {
  const { t } = useTranslation();
  const candidates = useIBSCandidates();
  // Round 17 R3 -- the thread's "Open the admission" lands on this
  // record's row, opened, rather than the top of the register.
  const [params] = useSearchParams();
  const focusIbs = params.get("ibs");
  const [expanded, setExpanded] = useState<string | null>(focusIbs);
  const [showPromote, setShowPromote] = useState(false);
  const loaded = Array.isArray(candidates.data);
  useEffect(() => {
    if (!loaded || !focusIbs) return;
    document
      .querySelector(`[data-ibs="${focusIbs.replace(/["\\]/g, "")}"]`)
      ?.scrollIntoView?.({ block: "center" });
  }, [loaded, focusIbs]);

  if (candidates.isLoading) {
    return (
      <p data-testid="ibs-admission-loading" className="text-sm text-slate-500">
        {t("modules.twin.ibsAdmission.loading")}
      </p>
    );
  }
  if (candidates.isError) {
    return (
      <p data-testid="ibs-admission-error" className="text-sm text-rag-red">
        {t("modules.twin.ibsAdmission.error")}
      </p>
    );
  }

  const rows = candidates.data ?? [];

  return (
    <section data-testid="ibs-admission-view" className="flex flex-col gap-4">
      <header>
        <p className="text-[11px] uppercase tracking-wide text-slate-500">
          {t("modules.twin.ibsAdmission.kicker")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="ibs"
            size={22}
            className="me-2 inline-block align-[-0.12em] text-brand-brassDark"
          />
          {t("modules.twin.ibsAdmission.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t("modules.twin.ibsAdmission.intro")}
        </p>
      </header>

      <Explainer termKey="admission" className="max-w-3xl" />

      {/* A4/C9 -- say it before the button, and say what to do instead.
          Renders nothing when the Engine is on. */}
      <EngineOffBanner manualKey="modules.twin.ibsAdmission.manual.engineOff" />

      <div>
        {showPromote ? (
          <PromoteServiceForm onDone={() => setShowPromote(false)} />
        ) : (
          <button
            type="button"
            data-testid="promote-toggle"
            onClick={() => setShowPromote(true)}
            className="rounded border border-brand-brassDark px-3 py-1.5 text-sm font-semibold text-brand-brassDark"
          >
            {t("modules.twin.ibsAdmission.promote.toggle")}
          </button>
        )}
      </div>

      <ReviewsDuePanel />

      {rows.length === 0 ? (
        <p data-testid="ibs-admission-empty" className="text-sm text-slate-500">
          {t("modules.twin.ibsAdmission.empty")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-start text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">{t("modules.twin.ibsAdmission.col.ibs")}</th>
                <th className="px-3 py-2">
                  {t("modules.twin.ibsAdmission.col.status")}
                </th>
                <th className="px-3 py-2">
                  {t("modules.twin.ibsAdmission.col.decision")}
                </th>
                <th className="px-3 py-2 text-end">
                  {t("modules.twin.ibsAdmission.col.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((c) => (
                <CandidateRow
                  key={c.id}
                  candidate={c}
                  expanded={expanded === c.id}
                  onToggle={() => setExpanded((cur) => (cur === c.id ? null : c.id))}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CandidateRow({
  candidate,
  expanded,
  onToggle,
}: {
  candidate: IBSCandidate;
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const derive = useDeriveAdmission();
  const me = useMe();
  //: A4 -- the same predicate the endpoints behind these verbs use.
  const canScore = canRunOperations(me.data);
  const [manual, setManual] = useState(false);
  const decision = candidate.latest_decision;

  return (
    <>
      <tr data-testid="ibs-admission-row" data-ibs={candidate.id}>
        <td className="px-3 py-2">
          <button
            type="button"
            onClick={onToggle}
            className="text-start font-medium text-slate-900 hover:underline"
          >
            {candidate.name}
            {/* Round 11 F3 -- a separator in the TEXT, and a sentence
                saying what the word means. The register read "AML
                suspicious transaction reportingSOVEREIGN" to anything
                taking innerText, and SOVEREIGN was explained nowhere
                in the product. */}
            {/* ROUND 13 H, seventh round -- the words were never the
                problem; the typography was. These are catalogued
                phrases ("Catalogue", "NBS", "Regulator-named",
                "Sovereign") and `uppercase tracking-wide` printed
                them as CATALOGUE, NBS, REGULATOR-NAMED and
                SOVEREIGN, which reads as an enum token however
                carefully the word was chosen. Six rounds of
                rewording a string that was already right. */}
            {candidate.sovereign_flag && (
              <span
                title={t("modules.twin.ibsAdmission.sovereignWhy")}
                className="ms-2 rounded bg-brand-brass/15 px-1.5 py-0.5 text-[10px] font-medium text-brand-brassDark"
              >
                <span aria-hidden="true"> · </span>
                {t("modules.twin.ibsAdmission.sovereign")}
              </span>
            )}
          </button>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            {/* Round 9 E8 -- four rounds of `minimize_customer_harm`
                under every IBS name. The label existed in the admin
                adapter, on the other side of a module boundary. */}
            <span>{outcomeCategoryLabel(candidate.outcome_category)}</span>
            {candidate.provenance_stream && (
              <span
                data-testid="ibs-provenance-stream"
                title={t(
                  `modules.twin.ibsAdmission.streamWhy.${candidate.provenance_stream}`,
                )}
                className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500"
              >
                {t(`modules.twin.ibsAdmission.stream.${candidate.provenance_stream}`)}
              </span>
            )}
          </div>
          {/* ROUND 15 H10 -- "Catalogue", "NBS", "Regulator-named",
              "Sovereign" unexplained, eighth round: the explanation
              lived in a tooltip. Said beneath the chip. */}
          {candidate.provenance_stream && (
            <p
              data-testid="ibs-provenance-stream-why"
              className="mt-0.5 text-[10px] text-slate-400"
            >
              {t("modules.twin.ibsAdmission.streamExplained", {
                stream: t(
                  `modules.twin.ibsAdmission.stream.${candidate.provenance_stream}`,
                ),
                why: t(
                  `modules.twin.ibsAdmission.streamWhy.${candidate.provenance_stream}`,
                ),
              })}
            </p>
          )}
        </td>
        <td className="px-3 py-2">
          <span
            data-testid="ibs-admission-status"
            className={
              "inline-block rounded px-2 py-0.5 text-xs font-medium " +
              (STATUS_PILL[candidate.admission_status] ?? "bg-slate-100")
            }
          >
            {t(`modules.twin.ibsAdmission.status.${candidate.admission_status}`)}
          </span>
        </td>
        <td className="px-3 py-2">
          {/* C10 -- and a record two people have SIGNED is not "not
              yet derived", whatever its score sheet holds. That text
              reads as nothing having happened, on a row showing two
              signatures. A signed record shows its decision.

              C5 -- "Deferred" meant two different things.
              Promoting a service mints a placeholder decision so the
              record exists before anybody has judged it, and that
              placeholder is `deferred` -- which reads identically to a
              deferral somebody actually decided. A row nobody has
              scored says so instead. */}
          {decision &&
          Object.keys(decision.scores ?? {}).length === 0 &&
          !decision.cro_signoff_at ? (
            <span
              data-testid="ibs-admission-not-derived"
              className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
            >
              {t("modules.twin.ibsAdmission.notYetDerived")}
            </span>
          ) : decision ? (
            <span
              className={
                "inline-block rounded px-2 py-0.5 text-xs font-medium " +
                (DECISION_PILL[decision.inclusion_decision] ?? "bg-slate-100")
              }
            >
              {t(`modules.twin.ibsAdmission.decision.${decision.inclusion_decision}`)}
              {/* C6 -- the criterion labels were fixed and THIS field
                  was not: a business reader met "included ·
                  severity_irreversibility" in a governance record.
                  Found by extending the RTL sweep to every route,
                  which is a reminder that the same defect can survive
                  in a second field on the same page. */}
              {decision.triggers_fired.length > 0 && (
                <>
                  {" · "}
                  <Bdi>
                    {decision.triggers_fired
                      .map((k) => CRITERION_LABEL[k] ?? k)
                      .join(" / ")}
                  </Bdi>
                </>
              )}
            </span>
          ) : (
            <></>
          )}
          {decision?.criticality_tier && " "}
          {decision?.criticality_tier && (
            <span
              data-testid="ibs-admission-tier"
              data-tier={decision.criticality_tier}
              className={
                "ms-2 inline-block rounded px-2 py-0.5 text-xs font-semibold " +
                (TIER_META[decision.criticality_tier]?.tone ?? "bg-slate-100")
              }
            >
              {TIER_META[decision.criticality_tier]?.label}
            </span>
          )}
          {!decision && (
            <span className="text-xs text-slate-400">
              {t("modules.twin.ibsAdmission.noDecision")}
            </span>
          )}
        </td>
        {/* A4 -- scoring is the resilience desk's work. The owner's own
            register row offered him "Derive (Engine)" and "Score
            manually" on his service, and the endpoints behind both
            refuse him. He reads his row here; the ask that IS his
            reaches him in My Work. */}
        <td className="px-3 py-2 text-end">
          {!canScore && (
            <span
              data-testid="ibs-admission-read-only"
              className="text-xs text-slate-400"
            >
              {t("modules.twin.ibsAdmission.readOnlyForYou")}
            </span>
          )}
          {canScore && (
            <span className="inline-flex gap-2">
              <button
                type="button"
                data-testid="ibs-admission-derive"
                disabled={derive.isPending}
                onClick={() =>
                  // E2 -- derive, challenge, sign and remove all finished
                  // without a word, so the operator pressed again or went
                  // looking on another page for whether it had worked.
                  derive.mutate(
                    { ibsId: candidate.id },
                    {
                      onSuccess: () =>
                        toast.push({
                          tone: "success",
                          message: t("modules.twin.ibsAdmission.derivedToast", {
                            name: candidate.name,
                          }),
                        }),
                      onError: (e) =>
                        toast.push({
                          tone: e.status === 503 ? "warning" : "error",
                          message:
                            e.status === 503
                              ? t("modules.twin.ibsAdmission.engineOff")
                              : // Round 16 A25 -- the server's sentence.
                                refusalMessage(t, e),
                        }),
                    },
                  )
                }
                className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
              >
                {derive.isPending
                  ? t("modules.twin.ibsAdmission.deriving")
                  : t("modules.twin.ibsAdmission.derive")}
              </button>
              <button
                type="button"
                data-testid="ibs-admission-score-manually"
                onClick={() => setManual((v) => !v)}
                className="ms-2 rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
              >
                {manual
                  ? t("modules.twin.ibsAdmission.manual.hide")
                  : t("modules.twin.ibsAdmission.manual.open")}
              </button>
            </span>
          )}
        </td>
      </tr>
      {derive.isError && (
        <tr>
          <td
            colSpan={4}
            data-testid="ibs-admission-derive-error"
            className="px-3 pb-2 text-xs text-rag-red"
          >
            {derive.error?.status === 503
              ? t("modules.twin.ibsAdmission.engineUnconfigured")
              : refusalMessage(t, derive.error)}
            {/* Round 16 A25 -- the sentence says to score it by hand, so
                the way to do that sits beside it. */}
            {derive.error?.status !== 503 && !manual && (
              <button
                type="button"
                data-testid="ibs-admission-derive-error-manual"
                onClick={() => setManual(true)}
                className="ms-2 rounded border border-slate-300 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
              >
                {t("modules.twin.ibsAdmission.manual.open")}
              </button>
            )}
          </td>
        </tr>
      )}
      {/* A4 -- the Engine being off used to end the road here. */}
      {manual && (
        <tr>
          <td colSpan={4} className="bg-brand-brass/5 px-3 py-3">
            <ManualAdmissionScores
              ibsId={candidate.id}
              pathway={candidate.pathway ?? "ces"}
              pathwayReason={candidate.pathway_reason ?? null}
            />
          </td>
        </tr>
      )}
      {expanded && decision && (
        <tr>
          <td colSpan={4} className="bg-slate-50/60 px-3 py-3">
            <DecisionDetail candidate={candidate} decision={decision} />
          </td>
        </tr>
      )}
    </>
  );
}

function DecisionDetail({
  candidate,
  decision,
}: {
  candidate: IBSCandidate;
  decision: IBSAdmissionDecision;
}): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  return (
    <div className="flex flex-col gap-4">
      {/* Round 20 Y1 -- offered to the seat that may request it; the
          internal audit seat was shown it on every row. */}
      {candidate.admission_status === "admitted" && canRunOperations(me.data) && (
        <RemovalAffordance candidate={candidate} />
      )}
      <NbsGatePanel decision={decision} />
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("modules.twin.ibsAdmission.scores")} ({decision.pathway})
          </h3>
          {decision.regime_code && (
            <p
              data-testid="ibs-decision-rubric"
              className="mb-1 text-[10px] text-slate-500"
            >
              {decision.rubric_version
                ? t("modules.twin.ibsAdmission.decidedUnder", {
                    regime: decision.regime_code.toUpperCase(),
                    version: decision.rubric_version,
                  })
                : t("modules.twin.ibsAdmission.decidedBeforeRubric", {
                    regime: decision.regime_code.toUpperCase(),
                  })}
            </p>
          )}
          {/* I2 -- once for the block. Every criterion's reasoning is
              Engine prose; a note per criterion would repeat it eight
              times in one panel. */}
          <EngineLanguageNote />
          <dl className="flex flex-col gap-1.5 text-xs">
            {Object.entries(decision.scores).map(([k, v]) => {
              const prov = decision.score_provenance?.[k];
              return (
                <div key={k} className="flex flex-col gap-0.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <dt className="text-slate-500">{CRITERION_LABEL[k] ?? k}</dt>
                    <dd className="text-end">
                      <span className="font-medium text-slate-800">{v}</span>
                      {CRITERION_ANCHORS[k]?.[v] && (
                        <span className="ms-1 text-[10px] text-slate-400">
                          {CRITERION_ANCHORS[k][v]}
                        </span>
                      )}
                    </dd>
                  </div>
                  {prov && (
                    // P218 (gap 5): evidence-pack provenance -- the data
                    // extract + a muted "source · date pulled" caption.
                    // P239: the data_extract is the EVIDENCE (figures); the
                    // rationale (reasoning) shows as a separate line when it
                    // differs.
                    <div
                      data-testid={`ibs-score-provenance-${k}`}
                      className="ps-2 text-[10px] text-slate-500"
                    >
                      {prov.data_extract && <span>{prov.data_extract}</span>}
                      {(() => {
                        const why = decision.rationale_by_criterion?.[k];
                        return why && why !== prov.data_extract ? (
                          <span
                            data-testid={`ibs-score-rationale-${k}`}
                            className="mt-0.5 block text-slate-500"
                          >
                            {t("modules.twin.ibsAdmission.because", { why })}
                          </span>
                        ) : null;
                      })()}
                      <span className="mt-0.5 block italic text-slate-400">
                        {prov.query_reference} · {prov.date_pulled}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </dl>
          <OperatingProfile tier={decision.criticality_tier} />
        </div>
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("modules.twin.ibsAdmission.thresholds")}
          </h3>
          <dl className="text-xs">
            <Threshold label="RTO" value={decision.threshold_rto_hours} unit="h" />
            <Threshold label="RPO" value={decision.threshold_rpo_hours} unit="h" />
            <Threshold
              label={t("modules.twin.ibsAdmission.maxOutage")}
              value={decision.threshold_max_outage_hours}
              unit="h"
            />
            {decision.threshold_impact_tolerance && (
              <div className="mt-1 text-slate-600">
                {decision.threshold_impact_tolerance}
              </div>
            )}
          </dl>
          <p
            className="mt-1 text-[10px] italic text-slate-500"
            data-testid="ibs-threshold-note"
          >
            {decision.workflow_state !== "approved"
              ? t("modules.twin.ibsAdmission.placeholderNote")
              : decision.thresholds_superseded_by
                ? t("modules.twin.ibsAdmission.placeholderSuperseded", {
                    hours: decision.thresholds_superseded_by.hours,
                    signedBy: decision.thresholds_superseded_by.signed_by ?? "",
                  })
                : t("modules.twin.ibsAdmission.placeholderUnsuperseded")}
          </p>
        </div>
        <GovernancePanel candidate={candidate} decision={decision} />
      </div>
      {/* P272.2 (UX-2 J2-5): the expanded admission row had ZERO outbound
          links -- a dead end. Point the operator at the next stages of the
          IBS's own journey: declare its impact tolerance (the Foundation
          drawer, deep-linked via ?edit=) + score it for MVB. */}
      <div
        data-testid="ibs-admission-next-steps"
        className="flex flex-wrap gap-x-5 gap-y-1 border-t border-slate-200 pt-2 text-xs"
      >
        {candidate.admission_status === "admitted" && (
          <Link
            to={`/foundation/admin/ibses?edit=${candidate.id}`}
            data-testid="ibs-admission-declare-tolerance"
            className="font-medium text-brand-brassDark hover:underline"
          >
            {t("modules.twin.ibsAdmission.nextSteps.declareTolerance")}
          </Link>
        )}
        {/* C3 -- not beside a ceremony that CONCLUDED it is not an IBS.
            MVB scoring asks how much of an admitted service the bank
            must stand up first, so offering it next to a signed
            deferral invites work on something the same page has just
            said is not one -- the same contradiction the sentence above
            carried.
            Deliberately narrow: an undecided candidate still gets the
            link, because there the register is somewhere to go rather
            than a claim about this service, and a broader gate broke
            exactly that case. */}
        {!(
          candidate.latest_decision?.workflow_state === "approved" &&
          (candidate.latest_decision?.inclusion_decision ?? "included") !== "included"
        ) && (
          <Link
            to="/twin/mvb-candidates"
            data-testid="ibs-admission-score-mvb"
            className="font-medium text-brand-brassDark hover:underline"
          >
            {t("modules.twin.ibsAdmission.nextSteps.scoreMvb")}
          </Link>
        )}
      </div>
    </div>
  );
}

// P221: reverse-admission -- deliberately remove an admitted IBS. Mints
// an excluded decision; the existing challenge + dual sign-off (rendered
// by GovernancePanel on the resulting draft) then governs it.
function RemovalAffordance({ candidate }: { candidate: IBSCandidate }): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const removal = useRequestRemoval();
  const [open, setOpen] = useState(false);
  const [rationale, setRationale] = useState("");
  return (
    <div
      data-testid="ibs-removal-affordance"
      className="rounded border border-rose-200 bg-rose-50/50 px-3 py-2 text-xs"
    >
      {!open ? (
        <button
          type="button"
          data-testid="ibs-removal-open"
          onClick={() => setOpen(true)}
          className="font-medium text-rose-700 hover:underline"
        >
          {t("modules.twin.ibsAdmission.removal.open")}
        </button>
      ) : (
        <div className="flex flex-col gap-2">
          <span className="font-semibold uppercase tracking-wide text-rose-700">
            {t("modules.twin.ibsAdmission.removal.heading")}
          </span>
          <p className="text-rose-700/80">
            {t("modules.twin.ibsAdmission.removal.intro")}
          </p>
          <textarea
            dir="auto"
            data-testid="ibs-removal-rationale"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={2}
            placeholder={t("modules.twin.ibsAdmission.removal.placeholder")}
            className="rounded border border-rose-200 bg-white px-2 py-1.5"
          />
          {removal.isError && (
            <p data-testid="ibs-removal-error" className="text-rose-700">
              {removal.error.detail}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="ibs-removal-submit"
              disabled={rationale.trim() === "" || removal.isPending}
              onClick={() =>
                removal.mutate(
                  { ibsId: candidate.id, rationale },
                  {
                    onSuccess: () => {
                      setOpen(false);
                      toast.push({
                        tone: "success",
                        message: t("modules.twin.ibsAdmission.removalToast", {
                          name: candidate.name,
                        }),
                      });
                    },
                  },
                )
              }
              className="rounded bg-rose-700 px-3 py-1 font-semibold text-white disabled:opacity-40"
            >
              {removal.isPending
                ? t("modules.twin.ibsAdmission.removal.submitting")
                : t("modules.twin.ibsAdmission.removal.submit")}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded border border-rose-200 px-3 py-1 text-rose-700"
            >
              {t("modules.twin.ibsAdmission.removal.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// P219 (gap 2): the evidence behind the binary NBS gate -- Q1/Q2 answers
// grounded in the IBS's real revenue + service financial impact, with an
// Engine-vs-evidence disagreement flag on Q2.
function NbsGatePanel({
  decision,
}: {
  decision: IBSAdmissionDecision;
}): JSX.Element | null {
  const { t } = useTranslation();
  const ev = decision.nbs_gate_evidence;
  if (!ev) return null;
  const yn = (b: boolean) => (b ? t("common.yes") : t("common.no"));
  // P416 -- the slots hold the rubric's own gates; name them as the
  // rubric does when they are not SAMA's Q1/Q2.
  const gateLabel = (slot: "q1" | "q2", fallbackKey: string) => {
    const code = ev.gate_codes?.[slot];
    return code &&
      code !== `${slot}_${slot === "q1" ? "external_customer" : "client_impact"}`
      ? t(`modules.twin.ibsAdmission.criterion.${code}`)
      : t(fallbackKey);
  };
  return (
    <div
      data-testid="ibs-nbs-gate"
      className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
    >
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("modules.twin.ibsAdmission.nbsGate")}
      </h3>
      <div className="flex flex-col gap-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-slate-600">
            {gateLabel("q1", "modules.twin.ibsAdmission.q1Outcome")}
          </span>
          <span className="font-medium text-slate-800">
            {yn(ev.q1.engine_answer)}
            {" · "}
            <span className="ms-1 text-[10px] text-slate-400">
              {t("modules.twin.ibsAdmission.breadthScore", {
                score: ev.q1.breadth_score ?? "—",
              })}
            </span>
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-slate-600">
            {gateLabel("q2", "modules.twin.ibsAdmission.q2Material")}
          </span>
          <span className="font-medium text-slate-800">
            {yn(ev.q2.engine_answer)}
            {" · "}
            <span
              className="ms-1 text-[10px] text-slate-400"
              data-testid="ibs-gate-q2-basis"
            >
              {ev.q2.evidence_workaround_exists === undefined
                ? // Pre-reconciliation decision: it carries the old
                  // financial-materiality shape, so show what it has.
                  ev.q2.financial_basis_sar
                  ? t("modules.twin.ibsAdmission.gateBasis", {
                      basis: formatSAR(ev.q2.financial_basis_sar),
                      hours: ev.q2.impact_tolerance_hours,
                      threshold: formatSAR(ev.q2.threshold_sar ?? "0"),
                    })
                  : t("modules.twin.ibsAdmission.gateBasisUnknown")
                : ev.q2.evidence_workaround_exists === null
                  ? t("modules.twin.ibsAdmission.gateWorkaroundUnknown")
                  : ev.q2.evidence_workaround_exists
                    ? t("modules.twin.ibsAdmission.gateWorkaroundYes")
                    : t("modules.twin.ibsAdmission.gateWorkaroundNo", {
                        names: (ev.q2.services_without_workaround ?? [])
                          .concat(ev.q2.services_with_stale_proof ?? [])
                          .join(", "),
                      })}
            </span>
          </span>
        </div>
        {/* Only claim disagreement when a basis actually exists to
            disagree with -- `consistent` is null when we couldn't ground
            Q2 at all, and `!null` would have fired this falsely (P296). */}
        {/* ROUND 15 A16 -- the answer against its own reasoning. The
            invented evidence went in round 14; the contradiction did
            not: "Yes" to material impact with a rationale that says a
            workaround exists. A narrow, stated rule rather than a
            judgement: the word the reasoning used. */}
        {ev.q2.engine_answer &&
          /workaround/i.test(
            decision.rationale_by_criterion?.q2_client_impact ?? "",
          ) && (
            <p
              data-testid="ibs-nbs-gate-q2-reasoning"
              className="mt-0.5 rounded bg-rag-amber/10 px-2 py-1 text-[10px] text-amber-700"
            >
              {t("modules.twin.ibsAdmission.gateReasoningSaysWorkaround")}
            </p>
          )}
        {ev.q2.consistent === false && (
          <p
            data-testid="ibs-nbs-gate-flag"
            className="mt-0.5 rounded bg-rag-amber/10 px-2 py-1 text-[10px] text-amber-700"
          >
            {t("modules.twin.ibsAdmission.gateDisagree")}
          </p>
        )}
        {/* Round 20 G45 -- the challenge's answer, or that there is none. */}
        {ev.q2.consistent === false &&
          (ev.q2.reconciliation ? (
            <p
              data-testid="ibs-nbs-gate-reconciled"
              className="mt-0.5 rounded bg-slate-100 px-2 py-1 text-[10px] text-slate-700"
            >
              {t("modules.twin.ibsAdmission.gateReconciled", {
                text: ev.q2.reconciliation.text,
                by: ev.q2.reconciliation.by ?? "",
              })}
            </p>
          ) : (
            decision.challenge_at && (
              <p
                data-testid="ibs-nbs-gate-unreconciled"
                className="mt-0.5 rounded bg-rag-amber/10 px-2 py-1 text-[10px] font-medium text-amber-800"
              >
                {t("modules.twin.ibsAdmission.gateNotReconciled")}
              </p>
            )
          ))}
        {ev.impact_magnitude?.financial_basis_sar && (
          <p
            data-testid="ibs-gate-magnitude"
            className="mt-0.5 text-[10px] text-slate-500"
          >
            {t("modules.twin.ibsAdmission.gateMagnitude", {
              basis: formatSAR(ev.impact_magnitude.financial_basis_sar),
              hours: ev.impact_magnitude.impact_tolerance_hours,
              threshold: formatSAR(ev.impact_magnitude.threshold_sar),
            })}
          </p>
        )}
        {ev.q2.basis_unavailable_reason && (
          <p
            data-testid="ibs-nbs-gate-ungrounded"
            className="mt-0.5 rounded bg-slate-200/60 px-2 py-1 text-[10px] text-slate-600"
          >
            {t("modules.twin.ibsAdmission.gateUngrounded")}
          </p>
        )}
      </div>
    </div>
  );
}

function Threshold({
  label,
  value,
  unit,
}: {
  label: string;
  value: number | null;
  unit: string;
}): JSX.Element {
  return (
    <div className="flex justify-between">
      <dt className="text-slate-500">{label}</dt>
      <dd className="font-medium text-slate-800">
        {value === null ? "—" : `${value}${unit}`}
      </dd>
    </div>
  );
}

function OperatingProfile({ tier }: { tier: string | null }): JSX.Element | null {
  const { t } = useTranslation();
  const meta = tier ? TIER_META[tier] : undefined;
  if (!meta) return null;
  return (
    <div
      data-testid="ibs-admission-profile"
      className="mt-2 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600"
    >
      <span className="font-semibold text-slate-700">{meta.label}</span>{" "}
      {t("screens.iBSAdmissionView.bizCrit")} {meta.bizCrit} · {meta.drill}{" "}
      {t("screens.iBSAdmissionView.refresh")} {meta.refresh}
    </div>
  );
}

/**
 * The ceremony's own state, in ceremony words.
 *
 * Round 10 F2 -- deliberately NOT `stateLabel`: that catalogue renders
 * `approved` as "Approved", which is the word the admission OUTCOME
 * also uses, and the collision is the whole defect. An unrecognised
 * state falls back to the catalogue rather than disappearing.
 */
const CEREMONY_STATES: readonly string[] = [
  "draft",
  "challenged",
  "cro_signed",
  "business_owner_signed",
  "approved",
];

function ceremonyState(
  raw: string | null | undefined,
  t: (key: string) => string,
): string {
  const key = (raw ?? "").trim().toLowerCase();
  return CEREMONY_STATES.includes(key)
    ? t(`modules.twin.ibsAdmission.signoff.ceremonyState.${key}`)
    : stateLabel(raw);
}

function GovernancePanel({
  candidate,
  decision,
}: {
  candidate: IBSCandidate;
  decision: IBSAdmissionDecision;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const approved = decision.workflow_state === "approved";
  const challenged = decision.challenge_at !== null;
  // A2 -- the second-line challenge is the reviewer's opinion, and the
  // form used to render for anyone who could reach the register. The
  // head of ops res recorded it under her own name, which is the wrong
  // signature on a record an examiner reads. `holdsSeat` excludes an
  // admin on purpose: reaching this page is not holding the seat.
  const isReviewer = holdsSeat(me.data, "reviewer");
  // Round 16 G12 -- "Waiting on the reviewer" answered why and not who
  // to chase, and it stood over a candidate nobody had scored, where
  // nothing waits on the reviewer yet.
  const seats = useSeatHolders();
  const scored = Object.keys(decision.scores ?? {}).length > 0;
  const reviewers = seats.data?.holders?.["reviewer"];
  const awaitingLine = !scored
    ? t("modules.twin.ibsAdmission.challenge.awaitingScores")
    : reviewers && reviewers.length === 1
      ? t("modules.twin.ibsAdmission.challenge.awaitingNamed", {
          name: reviewers[0],
          since: formatDateTime(decision.decided_at, i18n.language),
        })
      : reviewers && reviewers.length === 0
        ? t("modules.twin.ibsAdmission.challenge.awaitingUnheld")
        : t("modules.twin.ibsAdmission.challenge.awaitingReviewer");

  return (
    <div data-testid="ibs-admission-governance">
      {/* Round 10 F2 -- "GOVERNANCE · APPROVED" sat three characters
          above "Signed off -- deferred; no admission decision yet.",
          eighth round, because this heading printed the CEREMONY's
          column value while everything around it is keyed on the
          DECISION. Both were true and the pair reads as a
          contradiction: the signatures were given, and what they
          recorded was a deferral.

          So the heading says what the ceremony has reached, in
          ceremony words, and never borrows a word the outcome also
          uses. */}
      <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {t("modules.twin.ibsAdmission.signoff.title")} ·{" "}
        <span data-testid="ibs-admission-ceremony-state">
          {ceremonyState(decision.workflow_state, t)}
        </span>
      </h3>
      {!challenged && !approved ? (
        isReviewer ? (
          <ChallengeForm candidate={candidate} decision={decision} />
        ) : (
          <p
            data-testid="ibs-admission-awaiting-reviewer"
            className="text-[11px] text-slate-500"
          >
            {awaitingLine}
          </p>
        )
      ) : (
        <>
          {challenged && (
            <p
              data-testid="ibs-admission-challenged-by"
              className="mb-1 text-[11px] text-slate-500"
            >
              {t("modules.twin.ibsAdmission.challenge.by", {
                name: decision.challenge_reviewer_name ?? "",
              })}
            </p>
          )}
          {/* Round 20 A46 -- times earlier than the row they sit on. */}
          {(decision.signed_before_created?.length ?? 0) > 0 && (
            <p
              data-testid="ibs-admission-backdated"
              className="mb-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
            >
              {t("modules.twin.ibsAdmission.challenge.backdated", {
                legs: (decision.signed_before_created ?? [])
                  .map((leg) =>
                    leg === "challenge"
                      ? t("modules.twin.ibsAdmission.challenge.backdatedChallenge")
                      : roleLabel(leg),
                  )
                  .join(", "),
                created: decision.created_at
                  ? formatDateTime(decision.created_at, i18n.language)
                  : "—",
              })}
            </p>
          )}
          {/* Round 20 V1 -- the challenger also signed: the row read
              "both signatures recorded" over a challenge nobody else
              made. */}
          {decision.challenger_signed && (
            <p
              data-testid="ibs-admission-not-independent"
              className="mb-1 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-800"
            >
              {t("modules.twin.ibsAdmission.challenge.notIndependent", {
                name: decision.challenge_reviewer_name ?? "",
                role: roleLabel(decision.challenger_signed),
              })}
            </p>
          )}
          <SignoffPanel candidate={candidate} decision={decision} />
          {/* B1 -- what the signers actually said. The record showed
              "Challenged by Rashid Reviewer" and neither his words nor
              the CRO's, though every one of them is stored in its own
              column. A reservation the signer believed they recorded,
              which the record then does not show, is worse than never
              offering the box. */}
          <div className="mt-2">
            <SignatureHistory
              testid="ibs-admission-signatures"
              entries={[
                {
                  roleLabel: t("roles.challenge"),
                  name: decision.challenge_reviewer_name ?? null,
                  at: decision.challenge_at ?? null,
                  note: decision.challenge_note ?? null,
                },
                {
                  roleLabel: t("roles.cro"),
                  name: decision.cro_signoff_name ?? null,
                  at: decision.cro_signoff_at ?? null,
                  note: decision.cro_signoff_note ?? null,
                  // Round 10 F2 -- the sign form offers no box. The
                  // column exists and nothing has ever sent one, so
                  // "signed without a comment" was reporting a refusal
                  // nobody was given the chance to make.
                  commentOffered: false,
                },
                {
                  roleLabel: t("roles.business_owner"),
                  name: decision.business_owner_signoff_name ?? null,
                  at: decision.business_owner_signoff_at ?? null,
                  note: decision.business_owner_signoff_note ?? null,
                  commentOffered: false,
                },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}

function ChallengeForm({
  candidate,
  decision,
}: {
  candidate: IBSCandidate;
  decision: IBSAdmissionDecision;
}): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const challenge = useChallengeAdmission();
  const seats = useSeatHolders();
  const me = useMe();
  // A5 -- the reviewer is the session. Only the note is typed.
  const [note, setNote] = useState("");
  // Round 20 G45 -- an open disagreement on Q2 is the challenge's to answer.
  const disagreement = decision.nbs_gate_evidence?.q2?.consistent === false;
  const [reconciliation, setReconciliation] = useState("");

  return (
    <form
      data-testid="ibs-admission-challenge-form"
      className="flex flex-col gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        challenge.mutate(
          {
            ibsId: candidate.id,
            decisionId: decision.id,
            ...(note ? { note } : {}),
            ...(disagreement && reconciliation.trim()
              ? { reconciliation: reconciliation.trim() }
              : {}),
          },
          {
            onSuccess: () =>
              toast.push({
                tone: "success",
                // Says who it goes to, not merely that it worked.
                // Round 18 N11 -- the person, where one holds the seat.
                message: nowWith(t, "cro", seatName(seats.data, "cro")),
              }),
          },
        );
      }}
    >
      <p className="text-[10px] uppercase tracking-wide text-slate-400">
        {t("modules.twin.ibsAdmission.challenge.required")}
      </p>
      <p
        data-testid="ibs-admission-challenge-as"
        className="text-[10px] text-slate-500"
      >
        {t("modules.twin.ibsAdmission.challenge.recordingAs", {
          name: me.data?.display_name ?? me.data?.username ?? "",
        })}
      </p>
      <input
        dir="auto"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t("modules.twin.ibsAdmission.challenge.note")}
        className="rounded border border-slate-300 px-2 py-1 text-xs"
      />
      {disagreement && (
        <label className="flex flex-col gap-0.5 text-[10px] text-amber-800">
          {t("modules.twin.ibsAdmission.challenge.reconcileLabel")}
          <textarea
            dir="auto"
            data-testid="ibs-admission-challenge-reconcile"
            value={reconciliation}
            onChange={(e) => setReconciliation(e.target.value)}
            rows={2}
            className="rounded border border-amber-300 px-2 py-1 text-xs text-slate-800"
          />
        </label>
      )}
      <button
        type="submit"
        data-testid="ibs-admission-challenge-submit"
        disabled={challenge.isPending || (disagreement && !reconciliation.trim())}
        className="rounded bg-brand-brass px-2 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
      >
        {t("modules.twin.ibsAdmission.challenge.submit")}
      </button>
      {challenge.isError && (
        <p className="text-[10px] text-rag-red">
          {t("modules.twin.ibsAdmission.challenge.failed")}
        </p>
      )}
    </form>
  );
}

function SignoffPanel({
  candidate,
  decision,
}: {
  candidate: IBSCandidate;
  decision: IBSAdmissionDecision;
}): JSX.Element {
  const { t } = useTranslation();
  const seats = useSeatHolders();
  const toast = useToast();
  const signoff = useSignoffAdmission();
  const me = useMe();
  // A2: the role picker used to offer both seats to anyone who could
  // open the page, so a reader picked one, signed, and met a refusal.
  // Offer only the seat this session holds -- and when it holds
  // neither, say who is being waited on instead of nothing.
  // A5 -- and a seat already signed is no longer offered. The register
  // kept showing "Sign off" to a CRO who had just signed, greyed out
  // because the empty name box disabled it -- which reads as a
  // permission problem rather than a job already done.
  const signed: Record<"cro" | "business_owner", boolean> = {
    cro:
      decision.workflow_state === "cro_signed" ||
      decision.workflow_state === "approved",
    business_owner:
      decision.workflow_state === "business_owner_signed" ||
      decision.workflow_state === "approved",
  };
  // Round 20 N15 -- the owner's leg accepts the recorded owner only;
  // holding the seat is not enough, and offering it ended in a 403.
  const signableRoles = (["cro", "business_owner"] as const).filter(
    (r) =>
      canSignAs(me.data, r) &&
      !signed[r] &&
      (r !== "business_owner" || candidate.viewer_is_owner === true),
  );
  const [role, setRole] = useState<"cro" | "business_owner">(signableRoles[0] ?? "cro");

  // C3 -- the ceremony finishing and the answer it reached are two
  // different facts, and this reported the first as though it were the
  // second. A fully-signed DEFERRAL read "Approved -- both signatures
  // recorded; IBS status updated", in green, directly above a
  // signatures block quoting the owner agreeing to defer it. Fifth
  // round for the contradiction, because each round fixed the wording
  // and left the branch reading workflow_state.
  //
  // The ceremony is complete either way; what it concluded is the
  // inclusion decision, and the tone follows the conclusion rather
  // than the fact of having reached one.
  const ceremonyComplete = decision.workflow_state === "approved";
  const outcome = decision.inclusion_decision ?? "included";
  const outcomeTone =
    outcome === "included"
      ? "text-rag-green"
      : outcome === "excluded"
        ? "text-rag-red"
        : "text-rag-amber";

  return (
    <div data-testid="ibs-admission-signoff">
      {ceremonyComplete ? (
        <p
          data-testid="ibs-admission-signoff-outcome"
          data-outcome={outcome}
          className={`text-xs ${outcomeTone}`}
        >
          {t(`modules.twin.ibsAdmission.signoff.completed.${outcome}`, {
            defaultValue: t("modules.twin.ibsAdmission.signoff.completedOther"),
          })}
        </p>
      ) : !signableRoles.length ? (
        <p
          data-testid="ibs-admission-signoff-waiting"
          className="text-xs text-slate-600"
        >
          {/* E2 -- this named both seats however far the ceremony had
              got, so after the CRO signed it still read "waiting on
              the CRO and the business owner" and the reader could not
              tell whether her signature had landed. Name who is still
              owed. */}
          {/* ROUND 15 G2 -- the person, where the bank's record names
              exactly one for the seat; the seat otherwise. */}
          {(() => {
            const owed = (["cro", "business_owner"] as const).filter((r) => !signed[r]);
            const named = owed.map((r) => {
              // Round 20 W3 -- the owner's leg is the register's owner;
              // named with no address, no seat is theirs to sign from.
              if (
                r === "business_owner" &&
                candidate.owner_name &&
                candidate.owner_bound === false
              )
                return t("modules.twin.ibsAdmission.signoff.ownerNotSeat", {
                  name: candidate.owner_name,
                });
              // Round 20 N15 -- the owner's leg waits on the owner, not
              // on whoever holds the seat.
              if (r === "business_owner") {
                if (!candidate.owner_name)
                  return t("modules.twin.ibsAdmission.signoff.ownerNone");
                return candidate.owner_holds_seat === false
                  ? t("modules.twin.ibsAdmission.signoff.ownerHoldsNoSeat", {
                      name: candidate.owner_name,
                    })
                  : t("modules.twin.ibsAdmission.signoff.ownerNamed", {
                      name: candidate.owner_name,
                    });
              }
              const holders = seats.data?.holders?.[r];
              return holders && holders.length === 1
                ? `${holders[0]} (${roleLabel(r)})`
                : holders && holders.length === 0
                  ? t("toleranceState.awaitingUnheld", { role: roleLabel(r) })
                  : null;
            });
            return named.every((n) => n !== null)
              ? t("modules.twin.ibsAdmission.signoff.waitingOnNamed", {
                  who: named.join(" and "),
                })
              : t("modules.twin.ibsAdmission.signoff.waitingOn", {
                  roles: owed.map((r) => roleLabel(r)).join(" and "),
                });
          })()}
        </p>
      ) : (
        <form
          className="flex flex-col gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            // A5 -- the signature is the session's. Nothing to type.
            signoff.mutate(
              { ibsId: candidate.id, decisionId: decision.id, role },
              {
                onSuccess: (d) =>
                  toast.push({
                    tone: "success",
                    message:
                      d.workflow_state === "approved"
                        ? t("modules.twin.ibsAdmission.approvedToast", {
                            name: candidate.name,
                          })
                        : role === "cro"
                          ? nowWith(t, "business_owner", candidate.owner_name, true)
                          : nowWith(t, "cro", seatName(seats.data, "cro"), true),
                  }),
              },
            );
          }}
        >
          <select
            data-testid="ibs-admission-signoff-role"
            value={role}
            onChange={(e) => setRole(e.target.value as "cro" | "business_owner")}
            className="rounded border border-slate-300 px-2 py-1 text-xs"
          >
            {signableRoles.map((r) => (
              <option key={r} value={r}>
                {r === "cro"
                  ? t("modules.twin.ibsAdmission.signoff.cro")
                  : t("modules.twin.ibsAdmission.signoff.businessOwner")}
              </option>
            ))}
          </select>
          <p
            data-testid="ibs-admission-signoff-as"
            className="text-[10px] text-slate-500"
          >
            {t("modules.twin.ibsAdmission.signoff.signingAs", {
              name: me.data?.display_name ?? me.data?.username ?? "",
            })}
          </p>
          <button
            type="submit"
            data-testid="ibs-admission-signoff-submit"
            disabled={signoff.isPending}
            className="rounded bg-brand-brass px-2 py-1 text-xs font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
          >
            {t("modules.twin.ibsAdmission.signoff.sign")}
          </button>
          {signoff.isError && (
            <p className="text-[10px] text-rag-red">
              {t("modules.twin.ibsAdmission.signoff.failed")}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

/** Round 18 N11 -- "It is now with Khalid Al-Otaibi (Business owner)"
 *  where the product knows the person; the seat where it does not. */
function nowWith(
  t: TFunction,
  role: string,
  name: string | null | undefined,
  signed = false,
): string {
  const base = signed
    ? "modules.twin.ibsAdmission.signedToast"
    : "modules.twin.ibsAdmission.challengedToast";
  return name
    ? t(`${base}Named`, { name, role: roleLabel(role) })
    : t(base, { role: roleLabel(role) });
}

function seatName(data: SeatHolders | undefined, seat: string): string | null {
  const h = seatHolder(data, seat);
  return h.kind === "named" ? h.name : null;
}
